import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/browser";

/**
 * Based partially on https://www.hooks.guide/community/promiseHook
 * @param promiseFunc
 * @param inputs - same as useEffect
 */
function usePromise<T = any>(
  promiseFunc: () => Promise<T>,
  inputs: any[] = []
): [T, IUsePromiseBag<T>] {
  const [result, setResult] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [error, setError] = useState<Error | null>(null);

  /*
   * we need a way to (un)set the loading flag
   * ONLY after other state has updated, so let's use a ref and useEffect
   */
  const shouldSetLoading = useRef(true);
  useEffect(() => {
    if (shouldSetLoading.current !== loading) {
      setLoading(shouldSetLoading.current);
    }
  });

  function runPromise({ useLoadingFlag = true } = {}, callback = () => {}) {
    /*
      Using isValid guard, in order to prevent the cleanup warning.
    */
    let isValid = true;

    if (useLoadingFlag) {
      setLoading(true);
    }
    promiseFunc()
      .then(promiseResult => {
        if (!isValid) {
          return;
        }
        setResult(promiseResult);
        setLastUpdated(Date.now());
        shouldSetLoading.current = false;
        callback();
      })
      .catch(e => {
        if (!isValid) {
          return;
        }
        Sentry.captureException(e);
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error(e);
        }
        setError(e);
        shouldSetLoading.current = false;
        callback();
      });

    /*
      When component will be unmounted, isValid will become false and state setter
      functions will not be invoked on unmounted component.
    */
    return () => {
      isValid = false;
    };
  }

  useEffect(runPromise, inputs);

  return [
    result as T,
    {
      result,
      setResult,
      error,
      loading,
      lastUpdated,
      /*
       * To be able to await a reload we create a returnable promise here since runPromise
       * can't return a promise because it needs to return a cleanup func for useEffect
       */
      reload: (useLoadingFlag = false) =>
        new Promise(resolve => runPromise({ useLoadingFlag }, resolve)),
    },
  ];
}

export default usePromise;



// WEBPACK FOOTER //
// ./app/javascript/components/hooks/usePromise.ts